import { RouteObject, createBrowserRouter } from "react-router-dom"
import { AdminBookmarksScreen } from "~/admin/admin-bookmarks-screen"
import { AdminDashboardScreen } from "~/admin/admin-dashboard-screen"
import { AdminExclusionRulesScreen } from "~/admin/admin-exclusion-rules-screen"
import { AdminGroupDetailScreen } from "~/admin/admin-group-detail-screen"
import { AdminGroupsScreen } from "~/admin/admin-groups-screen"
import { AdminTagsScreen } from "~/admin/admin-tags-screen"
import { AdminUserDetailScreen } from "~/admin/admin-user-detail-screen"
import { AdminUsersScreen } from "~/admin/admin-users-screen"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { MinimalLayout } from "~/auth/minimal-layout"
import { BookmarkDetailScreen } from "~/bookmarks/bookmark-detail-screen"
import { BookmarkEditScreen } from "~/bookmarks/bookmark-edit-screen"
import { BookmarkIndexScreen } from "~/bookmarks/bookmark-index-screen"
import * as paths from "~/common/paths"
import { GroupDetailScreen } from "~/groups/group-detail-screen"
import { GroupIndexScreen } from "~/groups/group-index-screen"
import { GroupsLayout } from "~/groups/groups-layout"
import { PublicGroupsScreen } from "~/groups/public-groups-screen"
import { HistoryScreen } from "~/history/history-screen"
import { InviteScreen } from "~/invite/invite-screen"
import { SandwichPageLayout } from "~/layouts/sandwich-page-layout"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { CredentialsLoginScreen } from "~/login/credentials-login-screen"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { PrivacyScreen } from "~/privacy/privacy-screen"
import { ProfileEditScreen } from "~/profile/profile-edit-screen"
import { ProfileScreen } from "~/profile/profile-screen"
import { SignupScreen } from "~/signup/signup-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"
import { RootScreen } from "./root-screen"
import { MinimalSidebarLayout } from "~/auth/minimal-sidebar-layout"
import { MinimalCenteredLayout } from "~/auth/minimal-centered-layout"

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: paths.dashboardPath.pattern,
    element: <AdminDashboardScreen />,
  },
  {
    path: paths.adminExclusionRulesPath.pattern,
    element: <AdminExclusionRulesScreen />,
  },
  {
    path: paths.adminBookmarksPath.pattern,
    element: <AdminBookmarksScreen />,
  },
  {
    path: paths.adminUsersPath.pattern,
    element: <AdminUsersScreen />,
  },
  {
    path: paths.adminGroupsPath.pattern,
    element: <AdminGroupsScreen />,
  },
  {
    path: paths.adminTagsPath.pattern,
    element: <AdminTagsScreen />,
  },
  {
    path: paths.adminGroupDetailPath.pattern,
    element: <AdminGroupDetailScreen />,
  },
  {
    path: paths.adminUserDetailPath.pattern,
    element: <AdminUserDetailScreen />,
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: paths.bookmarkEditPath.pattern,
    element: <BookmarkEditScreen />,
  },
  {
    path: paths.bookmarkDetailPath.pattern,
    element: <BookmarkDetailScreen />,
  },
  {
    path: paths.groupsPath.pattern,
    element: <GroupsLayout />,
    children: [
      {
        index: true,
        element: <GroupIndexScreen />,
      },
      {
        path: "public",
        element: <PublicGroupsScreen />,
      },
    ],
  },
  {
    path: paths.historyPath.pattern,
    element: <HistoryScreen />,
  },
  {
    path: paths.profilePath.pattern,
    element: <ProfileScreen />,
  },
  {
    path: paths.profileEditPath.pattern,
    element: <ProfileEditScreen />,
  },
]

const authenticatedFullWidthRoutes: Array<RouteObject> = [
  {
    path: paths.bookmarksPath.pattern,
    element: <BookmarkIndexScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: systemAdminAuthenticatedRoutes,
          },
        ],
      },
      {
        element: <SandwichPageLayout full={false} />,
        children: [
          {
            path: paths.groupDetailPath.pattern,
            element: <GroupDetailScreen />,
          },

          {
            path: paths.bookmarkDetailPath.pattern,
            element: <BookmarkDetailScreen />,
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <MinimalLayout />,
            children: [
              {
                path: paths.loginPath.pattern,
                element: <EmailLoginScreen />,
              },
              {
                path: paths.credentialsLoginPath.pattern,
                element: <CredentialsLoginScreen />,
              },
              {
                path: paths.emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
          {
            element: <MinimalSidebarLayout />,
            children: [
              {
                path: paths.signupPath.pattern,
                element: <SignupScreen />,
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SandwichPageLayout full={false} />,
            children: authenticatedRoutes,
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SandwichPageLayout full={true} />,
            children: authenticatedFullWidthRoutes,
          },
        ],
      },

      {
        element: <MinimalCenteredLayout />,
        children: [
          {
            path: paths.invitePath.pattern,
            element: <InviteScreen />,
          },
          {
            path: paths.privacyPath.pattern,
            element: <PrivacyScreen />,
          },
        ],
      },
      {
        path: paths.rootPath.pattern,
        element: <RootScreen />,
      },
    ],
  },
])
